import {
  Form,
  FormSubmitButton,
  SearchableSelectFormField,
  SelectFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { useReassignUserAssignments } from "@/lib/data/react-query";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { type MatterRole, type DirectoryUser } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { CircleHelp } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const FORM_ID = "user-reassignment-form";

interface UserReassignmentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const formSchema = z
  .object({
    oldUserId: z.string().min(1, { message: "Select a user" }),
    newUserId: z.string().min(1, { message: "Select a user" }),
    roleId: z.string().optional(),
  })
  .refine((data) => data.oldUserId !== data.newUserId, {
    message: "Select a different user",
    path: ["newUserId"],
  });

type FormValues = z.infer<typeof formSchema>;

const formatUserLabel = (user?: DirectoryUser) =>
  user ? `${user.firstName} ${user.lastName}` : undefined;

const formatUserContext = (user?: DirectoryUser) => user?.email ?? undefined;

export function UserReassignmentModal({
  open,
  setOpen,
}: UserReassignmentModalProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      oldUserId: "",
      newUserId: "",
      roleId: "",
    },
  });

  const reassignUserAssignments = useReassignUserAssignments();
  const { data: matterSettingsData, isError: isMatterSettingsError } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const oldUserId = form.watch("oldUserId");
  const newUserId = form.watch("newUserId");
  const oldUserDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [oldUserId],
  });
  const newUserDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [newUserId],
  });

  const availableOldUsers = useMemo(
    () => oldUserDirectory.users.filter((user) => user.id !== newUserId),
    [oldUserDirectory.users, newUserId],
  );

  const availableNewUsers = useMemo(
    () => newUserDirectory.users.filter((user) => user.id !== oldUserId),
    [newUserDirectory.users, oldUserId],
  );

  const handleSubmit = async (values: FormValues) => {
    const oldUser = oldUserDirectory.users.find(
      (user) => user.id === values.oldUserId,
    );
    const newUser = newUserDirectory.users.find(
      (user) => user.id === values.newUserId,
    );

    if (!oldUser || !newUser) {
      toast.error("Unable to find the selected users");
      return;
    }

    await reassignUserAssignments.mutateAsync({
      currentUserId: oldUser.id,
      replacementUserId: newUser.id,
      ...(values.roleId ? { roleId: values.roleId } : {}),
    });

    setOpen(false);
    form.reset({ oldUserId: "", newUserId: "", roleId: "" });
  };

  useEffect(() => {
    if (!open) {
      form.reset({ oldUserId: "", newUserId: "", roleId: "" });
    }
  }, [open, form]);

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        isDirty={form.formState.isDirty}
        title="Reassign user"
        titleTrailing={
          <TooltipWrapper message="All active matter assignments owned by the selected source user will be reassigned to the new user. When a matter role is selected, assignments in other roles stay with the current user. This action cannot be undone.">
            <button
              type="button"
              aria-label="About user reassignment"
              className="text-muted-foreground hover:text-foreground"
            >
              <CircleHelp className="size-4" />
            </button>
          </TooltipWrapper>
        }
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          formRef.current?.focus();
        }}
        description="Move matter ownership from one team member to another without editing individual matters. When a matter role is selected, only assignments in that role are moved; assignments in other roles stay with the current user."
        loading={reassignUserAssignments.isPending}
        size="xl"
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <FormCancelButton setOpen={setOpen} />
            <FormSubmitButton
              title="Reassign"
              variant="default"
              submitFormId={FORM_ID}
              loading={reassignUserAssignments.isPending}
              disabled={
                !form.formState.isValid || reassignUserAssignments.isPending
              }
            />
          </div>
        }
      >
        <form
          ref={formRef}
          id={FORM_ID}
          tabIndex={-1}
          className="flex min-h-0 grow flex-col overflow-hidden"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div className="flex grow flex-col gap-6 overflow-y-auto p-6">
            <fieldset
              disabled={reassignUserAssignments.isPending}
              className="grid grid-cols-2 gap-4"
            >
              <SearchableSelectFormField
                label="From user*"
                placeholder="Select current owner"
                register={form.register("oldUserId")}
                items={availableOldUsers}
                itemsKey="id"
                {...oldUserDirectory.selectProps}
                formatItemLabel={formatUserLabel}
                formatSelectedItemLabel={formatUserLabel}
                formatItemContext={formatUserContext}
              />
              <div>
                <SelectFormField<MatterRole>
                  label="From user's role (optional)"
                  placeholder="All matter roles"
                  register={form.register("roleId")}
                  items={matterSettingsData?.data.roles ?? []}
                  itemsKey="id"
                  isItemsError={isMatterSettingsError}
                  formatItemLabel={(role) => role?.label}
                  clearable
                />
              </div>
              <SearchableSelectFormField
                label="To user*"
                placeholder="Select new owner"
                register={form.register("newUserId")}
                items={availableNewUsers}
                itemsKey="id"
                {...newUserDirectory.selectProps}
                formatItemLabel={formatUserLabel}
                formatSelectedItemLabel={formatUserLabel}
                formatItemContext={formatUserContext}
              />
            </fieldset>
          </div>
        </form>
      </AppModal>
    </Form>
  );
}
